import { Cartao, ListaDeDados, type Dado } from '../../../components/ListaDeDados'
import { dataHora } from '../../../lib/datas'
import { TERMOS } from '../../../lib/glossario'
import type { DetalheChamado } from '../../../types/chamado'
import { EtiquetaDeSla } from '../etiquetas'

/**
 * As datas do chamado e os dois prazos do contrato.
 *
 * São dois prazos distintos, e o TR os separa (ANEXO IV, defs. 7 e 8, p.90): o de tempo de
 * ATENDIMENTO vai da abertura à primeira interação, e o de tempo de SOLUÇÃO vai da abertura à
 * resolução completa. Os dois são contados em horas úteis. Exibi-los sob um rótulo só faria a tela
 * dizer "o prazo" onde o contrato diz dois, e é o segundo que decide a situação de SLA.
 *
 * Resolução e fechamento também são coisas diferentes e podem estar em dias diferentes. Quem for
 * conferir por que um chamado ficou em maio e não em junho vai olhar exatamente para essas duas
 * linhas — a competência é o mês do fechamento.
 *
 * @param props o chamado
 * @returns o cartão de datas e prazos
 */
export function PainelDePrazos({ chamado }: { chamado: DetalheChamado }) {
  const datas: Dado[] = [
    { rotulo: 'Aberto em', valor: dataHora(chamado.abertoEm) },
    { rotulo: 'Primeira interação', valor: dataHora(chamado.primeiraInteracaoEm) },
    // O e-Niterói não devolve o INSTANTE da primeira interação (a linha acima fica vazia em todos
    // os chamados), mas devolve quanto tempo útil levou até ela. É essa duração que o contrato
    // define como tempo de atendimento, e é ela que se compara com o prazo da linha seguinte.
    { rotulo: 'Tempo até o primeiro atendimento', valor: horasUteis(chamado.tempoDeAtendimentoSegundos) },
    { rotulo: 'Prazo para a primeira resposta', valor: dataHora(chamado.prazoPrimeiraInteracao) },
    { rotulo: 'Resolvido em', valor: dataHora(chamado.resolvidoEm) },
    { rotulo: 'Fechado em', valor: dataHora(chamado.fechadoEm) },
    { rotulo: 'Prazo para resolver', ajuda: 'sla', valor: <PrazoDeSolucao chamado={chamado} /> },
    {
      rotulo: 'Situação diante do prazo',
      valor: <EtiquetaDeSla situacao={chamado.situacaoSla} rotulo={chamado.situacaoSlaRotulo} />,
    },
  ]

  return (
    <Cartao
      titulo={`Datas e ${TERMOS.sla.rotulo.toLowerCase()}`}
      descricao="Os dois prazos do contrato são contados em horas úteis, no calendário da Prefeitura."
    >
      <ListaDeDados dados={datas} />
    </Cartao>
  )
}

/**
 * Segundos de tempo útil como a tela os lê.
 *
 * Minutos abaixo de uma hora, porque o SLA inicial desta base é de 45 minutos: "0,8 horas úteis"
 * seria tecnicamente certo e inútil para quem confere. Ausente vira travessão — o campo é omitido
 * do JSON quando a fonte não informou, e testar `=== undefined` passaria por `null` sem ver.
 */
function horasUteis(segundos?: number) {
  if (!segundos && segundos !== 0) {
    return '—'
  }
  if (segundos < 3600) {
    return `${Math.round(segundos / 60)} min úteis`
  }
  return `${(Math.round(segundos / 360) / 10).toLocaleString('pt-BR')} horas úteis`
}

/**
 * O prazo de solução, ou a explicação da ausência dele.
 *
 * Prazo em branco não é registro incompleto: é serviço sem prazo de conclusão pactuado, situação
 * que o TR prevê (ANEXO IV, p.90 — SLA entre asteriscos vale para responder, não para concluir).
 * São 12 dos 64 chamados medidos no e-Niterói. Deixar a linha vazia mandaria alguém procurar o dado
 * perdido; escrever a explicação encerra a pergunta.
 */
function PrazoDeSolucao({ chamado }: { chamado: DetalheChamado }) {
  if (!chamado.prazoSla) {
    return <span className="text-slate-600">{TERMOS.semPrazoDeSla.ajuda}</span>
  }
  return <>{dataHora(chamado.prazoSla)}</>
}
